import * as Yup from "yup";
import "../accountsInfo.css";
import { useState } from "react";
import Toaster from "../../../components/Toast/Toast";
import { useDispatch, useSelector } from "react-redux";
import { toggleReFetch } from "../../../redux/slices/themeSlice";
import { postUserShippingDetails } from "../../../api/modules/b2droppie.class";

const ShippingAddress = () => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state?.auth?.user);
  const isDarkMode = useSelector((state) => state.theme.darkMode);
  const verificationStatus = useSelector(
    (state) => state?.auth?.user?.verification_status,
  );

  const initialFormData = {
    name: userData?.userdetails?.name || "",
    address_1: userData?.userdetails?.address_1 || "",
    address_2: userData?.userdetails?.address_2 || "",
    postal_code: userData?.userdetails?.postal_code || "",
    state: userData?.userdetails?.state || "",
    city: userData?.userdetails?.city || "",
    country: userData?.userdetails?.country || "",
    shipping_phone_number: userData?.userdetails?.shipping_phone_number || "",
  };

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [formData, setFormData] = useState(initialFormData);
  const [toast, setToast] = useState({ success: "", error: "" });

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .max(30, "Name cannot exceed 30 characters"),
    address_1: Yup.string().required("Address line 1 is required"),
    address_2: Yup.string().nullable(),
    postal_code: Yup.string().required("Postal code is required"),
    state: Yup.string()
      .required("State is required")
      .max(255, "State cannot exceed 255 characters"),
    city: Yup.string()
      .required("City is required")
      .max(255, "City cannot exceed 255 characters"),
    country: Yup.string().required("Country is required"),
    shipping_phone_number: Yup.string()
      .required("Phone number is required")
      .matches(/^\+?[0-9]{8,15}$/, "Invalid phone number format"),
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      validationSchema
        .validateAt(name, { ...formData, [name]: value })
        .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
        .catch((err) =>
          setErrors((prev) => ({ ...prev, [name]: err.message })),
        );
    }
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    validationSchema
      .validateAt(name, formData)
      .then(() => setErrors((prev) => ({ ...prev, [name]: "" })))
      .catch((err) => setErrors((prev) => ({ ...prev, [name]: err.message })));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormChanged) {
      setToast({
        success: "",
        error: "There are no changes to save",
      });
      return;
    }

    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setErrors({});

      const response = await postUserShippingDetails(formData);

      if (response?.success || response?.status === 200) {
        dispatch(toggleReFetch("mine"));
        setToast({
          success:
            response?.data?.message || "Shipping details saved successfully",
          error: "",
        });
      } else {
        throw new Error("Failed to save shipping details");
      }
    } catch (err) {
      if (err.name === "ValidationError") {
        const formattedErrors = {};
        const allTouched = {};

        err.inner.forEach((e) => {
          formattedErrors[e.path] = e.message;
          allTouched[e.path] = true;
        });

        setErrors(formattedErrors);
        setTouched((prev) => ({ ...prev, ...allTouched }));
      } else {
        setToast({
          success: "",
          error: err.message || "Something went wrong",
        });
      }
    }
  };

  const isFormChanged = Object.keys(initialFormData).some(
    (key) => initialFormData[key] !== formData[key],
  );

  return (
    <>
      <Toaster message={toast} />
      <div className={`user-info-outer ${isDarkMode ? "dark" : "light"}`}>
        <h2 className="title mb-0">Shipping Address</h2>

        <form onSubmit={handleSubmit} className="mt-32px">
          <div className="container p-0">
            <div className="row">
              {[
                { label: "Name", name: "name", placeholder: "Full Name" },
                {
                  label: "Phone Number",
                  name: "shipping_phone_number",
                  placeholder: "Phone Number",
                },
                {
                  label: "Address Line 1",
                  name: "address_1",
                  placeholder: "Address Line 1",
                },
                {
                  label: "Address Line 2 (optional)",
                  name: "address_2",
                  placeholder: "Address Line 2 (optional)",
                },
                { label: "City", name: "city", placeholder: "City" },
                {
                  label: "State/Province",
                  name: "state",
                  placeholder: "State/Province",
                },
                { label: "Country", name: "country", placeholder: "Country" },
                {
                  label: "Postal Code",
                  name: "postal_code",
                  placeholder: "Postal Code",
                },
              ].map(({ label, name, placeholder }) => (
                <div
                  key={name}
                  className="col-lg-6 col-md-12 col-12  form-group"
                  style={{ marginBottom: "20px" }}
                >
                  <label
                    className={`form-label ${
                      isDarkMode ? "form-label-dark" : ""
                    }`}
                  >
                    {label}
                  </label>
                  <input
                    type="text"
                    name={name}
                    onBlur={handleBlur}
                    value={formData[name]}
                    onChange={handleChange}
                    placeholder={placeholder}
                    className={`form-input ${errors[name] ? "error" : ""}`}
                  />
                  {errors[name] && (
                    <span className="error-message">{errors[name]}</span>
                  )}
                </div>
              ))}

              <div className="d-end gap-10px my-auto">
                <button
                  type="submit"
                  className="filled-medium-deep-yellow-btn-dark w-lg-20 w-md-50"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ShippingAddress;
